import React from 'react';
import { Sensor } from '../services/api';
import ThresholdSettings from './ThresholdSettings';

interface RangeValue {
  normalMin: number;
  normalMax: number;
}

interface RangeSettings {
  power_sensor: {
    temperature: RangeValue;
    humidity: RangeValue;
    battery: RangeValue;
  };
  water_sensor: {
    water_level: RangeValue;
  };
  smoke_sensor: {
    smoke: RangeValue;
  };
  rgb_camera: {
    motion: RangeValue;
    light: RangeValue;
  };
}

interface SensorDetailPanelProps {
  selectedSensor: Sensor;
  showThresholdSettings: boolean;
  setShowThresholdSettings: (show: boolean) => void;
  hlsUrl: string | null;
  streamFullscreen: boolean;
  setStreamFullscreen: (fullscreen: boolean) => void;
  hlsVideoRef: React.MutableRefObject<HTMLVideoElement | null>;
  onClose: () => void;
  rangeSettings: RangeSettings;
  handleRangeChange: (sensorType: string, param: string, newValue: RangeValue) => void;
  handleSaveThreshold: () => void;
  RangeSlider: React.FC<{
    min: number;
    max: number;
    value: RangeValue;
    onChange: (value: RangeValue) => void;
    label: string;
    unit: string;
    currentValue: number;
  }>;
}

const SensorDetailPanel: React.FC<SensorDetailPanelProps> = ({
  selectedSensor,
  showThresholdSettings,
  setShowThresholdSettings,
  hlsUrl,
  streamFullscreen,
  setStreamFullscreen,
  hlsVideoRef,
  onClose,
  rangeSettings,
  handleRangeChange,
  handleSaveThreshold,
  RangeSlider,
}) => {
  return (
    <div className="w-80 bg-white shadow-sm border-l border-gray-200 overflow-y-auto">
      <div className="p-4">
        {/* 面板標題 */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {showThresholdSettings ? '設定警報閥值' : '感測器詳情'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* 感測器詳情內容 */}
        {!showThresholdSettings && (
          <div className="space-y-4">
            {/* 有攝影機的感測器顯示即時影像，否則預設顯示 */}
            <div className="text-center">
              {hlsUrl ? (
                <div
                  className="w-full h-48 rounded mb-2 overflow-hidden bg-gray-900 relative cursor-pointer group"
                  onClick={() => setStreamFullscreen(true)}
                >
                  <video
                    ref={hlsVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute bottom-1 right-1 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    點擊放大
                  </div>
                </div>
              ) : (
                <div className="w-full h-32 bg-gradient-to-br from-blue-50 to-indigo-50 rounded mb-2 flex items-center justify-center">
                  <div className="text-center text-blue-600">
                    <div className="text-2xl mb-1">📡</div>
                    <div className="text-sm font-medium">感測器詳情</div>
                    <div className="text-xs text-blue-500 mt-1">{selectedSensor.name}</div>
                  </div>
                </div>
              )}
            </div>

            {/* 感測器資訊 */}
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">感測器名稱:</span>
                <span className="font-medium">{selectedSensor.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">類型:</span>
                <span>{selectedSensor.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">位置:</span>
                <span>{selectedSensor.location_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">GPS 位置:</span>
                <span className="font-mono text-xs">
                  {typeof selectedSensor.coordinates[0] === 'number' ? selectedSensor.coordinates[0].toFixed(6) : parseFloat(selectedSensor.coordinates[0] as unknown as string).toFixed(6)}, {typeof selectedSensor.coordinates[1] === 'number' ? selectedSensor.coordinates[1].toFixed(6) : parseFloat(selectedSensor.coordinates[1] as unknown as string).toFixed(6)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">IP 地址:</span>
                <span className="font-mono">{selectedSensor.ip}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">最後更新:</span>
                <span className="text-xs">{selectedSensor.lastUpdate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">當前狀態:</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  selectedSensor.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {selectedSensor.status === 'active' ? '正常' : '警報'}
                </span>
              </div>

              {selectedSensor.type === '電量感測器' && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">溫度:</span>
                    <span className="font-semibold text-orange-600">{selectedSensor.temperature ?? '-'} °C</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">電壓:</span>
                    <span className="font-semibold text-yellow-600">{selectedSensor.battery ?? '-'} V</span>
                  </div>
                </>
              )}
              {selectedSensor.type === '水位感測器' && (
                <div className="flex justify-between">
                  <span className="text-gray-600">水位:</span>
                  <span className="font-semibold text-blue-600">{selectedSensor.smoke_level ?? '-'} m</span>
                </div>
              )}
              {selectedSensor.type === '煙霧感測器' && (
                <div className="flex justify-between">
                  <span className="text-gray-600">煙霧濃度:</span>
                  <span className="font-semibold text-gray-600">{selectedSensor.smoke_level ?? '-'} ppm</span>
                </div>
              )}
            </div>

            {/* 操作按鈕 */}
            <div className="space-y-2 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowThresholdSettings(true);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium"
              >
                ⚙️ 設定警報閥值
              </button>
            </div>
          </div>
        )}

        {/* 閾值設定面板 */}
        {showThresholdSettings && (
          <ThresholdSettings
            selectedSensor={selectedSensor}
            rangeSettings={rangeSettings}
            handleRangeChange={handleRangeChange}
            handleSaveThreshold={handleSaveThreshold}
            setShowThresholdSettings={setShowThresholdSettings}
            RangeSlider={RangeSlider}
          />
        )}

        {/* 感測器管理面板 */}
      </div>
    </div>
  );
};

export default SensorDetailPanel;
